/**
 * Cliente do stream de eventos U1 (`wss://.../v1/u1/stream`).
 *
 * Quatro problemas concretos, resolvidos aqui e não espalhados pela interface:
 *
 * 1. **Reconexão.** Rede móvel cai, o navegador dorme a aba, o nginx recicla a
 *    conexão. Backoff exponencial com teto e ruído, para não sincronizar todos
 *    os clientes num mesmo instante depois de um deploy.
 * 2. **Duplicação.** A retomada reenvia eventos que o cliente já viu. O
 *    contrato dá `event_id` justamente para isso; deduplicamos por ele.
 * 3. **Retomada.** Guardamos o maior `seq` aplicado e pedimos `since=<seq>` na
 *    reconexão, para não perder o que aconteceu enquanto estávamos fora.
 * 4. **Fila local.** O que o usuário faz enquanto a conexão está caída não pode
 *    sumir; enfileiramos e despachamos ao reabrir.
 *
 * O que este cliente NÃO faz: interpretar eventos. Ele entrega envelopes
 * válidos e deixa a máquina de estados decidir.
 */

import {
  assertEnvelope,
  isU1Event,
  type U1Envelope,
  type U1Event,
  type U1EventType,
  type VoiceMode,
} from '@will/contracts';
import { urlDoStream } from './config';

// ---------------------------------------------------------------------------
// Tipos públicos
// ---------------------------------------------------------------------------

export type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export type EventHandler<P = unknown> = (evento: U1Event<P>) => void;
export type StatusHandler = (status: ConnectionStatus, detalhe?: string) => void;

/** Cancela uma inscrição. Guardar e chamar no cleanup do efeito. */
export type Unsubscribe = () => void;

/**
 * Mensagens do cliente para o runtime.
 *
 * TODO(contracts): o `@will/contracts` tipa hoje só o sentido servidor→cliente
 * (`U1Envelope`). Enquanto o sentido inverso não estiver no contrato, ele mora
 * aqui — e é o primeiro candidato a migrar, porque é exatamente o tipo de
 * divergência que a ADR-009 quis matar.
 */
export type ClientMessage =
  | { type: 'u1.client.text_turn'; text: string; trace_id?: string }
  // Restringe o que o Will pode usar nesta sessão. A lista nunca amplia: o
  // servidor a intersecciona com o que o papel da pessoa já permite, e devolve
  // em `u1.control.accepted` o que de fato valeu.
  | { type: 'u1.client.connectors'; enabled: readonly string[] }
  | { type: 'u1.client.interrupt' }
  | { type: 'u1.client.voice_mode'; mode: VoiceMode }
  | { type: 'u1.client.approval'; approval_id: string; granted: boolean }
  // Roda um passo do plano de uma tarefa. Vai só o id: o que aquele passo é —
  // ferramenta ou humano, com quais argumentos — já está no banco, e mandar
  // isso do navegador seria deixar o cliente redizer o plano que o servidor
  // guarda. O que a escrita exigir de aprovação continua passando pelo mesmo
  // portão de sempre; este evento não é um atalho por fora dele.
  | { type: 'u1.client.step_run'; step_id: string }
  // Tira um passo do plano de propósito — o que produz o estado `dispensado`,
  // que a migração 0009 declarava e ninguém conseguia alcançar. Vai só o id,
  // como no irmão de cima, e não vai motivo: esta tela não pergunta nenhum, e
  // campo que ninguém preenche é promessa que o produto não cumpre. Quem
  // dispensou é o servidor que escreve, porque é ele que sabe quem é o
  // portador do ticket.
  | { type: 'u1.client.step_dismiss'; step_id: string }
  | { type: 'u1.client.privacy'; enabled: boolean };

export interface BackoffOptions {
  baseMs: number;
  maxMs: number;
  fator: number;
  /**
   * Quanto tempo a conexão precisa ficar de pé para o backoff zerar. Sem essa
   * janela, um servidor que aceita e derruba na sequência viraria laço de
   * reconexão em intervalo mínimo.
   */
  estabilidadeMs: number;
}

export interface EventClientOptions {
  sessionId: string;
  ticket: string;
  /** sobrescreve a URL do stream — usado em teste */
  url?: string;
  /** retomada a partir de um seq conhecido (ex.: recarregou a página) */
  since?: number;
  maxDedupe?: number;
  maxFila?: number;
  backoff?: Partial<BackoffOptions>;
  /**
   * Compra credencial nova quando o handshake é recusado.
   *
   * Chamado antes de uma tentativa que veio depois de um fechamento **sem
   * abertura** — a assinatura de um upgrade recusado, que o navegador só
   * reporta como `close 1006`, sem código nem motivo. Sem isto o cliente
   * reapresenta o mesmo ticket vencido para sempre; foi o que aconteceu em
   * produção.
   *
   * Pode devolver uma sessão diferente: quando a antiga morreu, continuar nela
   * é impossível, e uma sessão nova é melhor que um laço.
   */
  renovar?: (sessionId: string) => Promise<{ ticket: string; sessionId: string }>;
  /** avisado quando a renovação trocou a sessão — o `session_id` mudou */
  onSessaoTrocada?: (sessionId: string) => void;
  /**
   * Todo quadro `u1.control.*`, cru, antes do tratamento interno.
   *
   * Existe porque `u1.control.accepted` diz o que de fato valeu de uma
   * mensagem do cliente, e até agora ninguém escutava: o painel mandava um
   * passo, o servidor respondia "aceito, o passo está feito", e a tela ficava
   * com o botão em "Executando…" até alguém recarregar a página. A resposta
   * chegava e morria aqui dentro — a mesma forma de defeito que este projeto
   * já pagou várias vezes, com o lado que expõe e o lado que não consome.
   *
   * Cru e genérico de propósito: um gancho por assunto (`onPasso`,
   * `onConectores`) faria esta classe conhecer o produto, e ela não conhece —
   * quem sabe o que fazer com um `accepted` é quem mandou a mensagem.
   */
  onControl?: (tipo: string, quadro: Record<string, unknown>) => void;
}

const BACKOFF_PADRAO: BackoffOptions = {
  baseMs: 500,
  maxMs: 15_000,
  fator: 1.8,
  estabilidadeMs: 3_000,
};

const MAX_DEDUPE_PADRAO = 1024;
const MAX_FILA_PADRAO = 64;

// ---------------------------------------------------------------------------

export class EventClient {
  readonly #url: string;
  /**
   * Não é `readonly`: quando a sessão morre, `renovar` devolve outra e este
   * cliente passa a falar com ela. Mantê-la fixa transformaria a morte da
   * sessão num laço de reconexão sem saída.
   */
  #sessionId: string;
  /**
   * Não é `readonly`: o runtime entrega um ticket novo pelo próprio stream
   * (`u1.control.ticket`) antes de o antigo vencer. Guardar o primeiro para
   * sempre faria toda reconexão depois de 120 s bater em 401 — e desde a v0.2
   * o ticket carrega tenant e usuário, então ele é a única credencial que o
   * handshake tem.
   */
  #ticket: string;
  readonly #backoff: BackoffOptions;
  readonly #maxDedupe: number;
  readonly #maxFila: number;
  readonly #renovar: EventClientOptions['renovar'];
  readonly #onSessaoTrocada: EventClientOptions['onSessaoTrocada'];
  readonly #onControl: EventClientOptions['onControl'];
  /** a próxima tentativa deve comprar credencial antes de abrir */
  #renovarAntes = false;
  /** uma renovação em voo por vez */
  #renovando = false;

  #socket: WebSocket | null = null;
  #status: ConnectionStatus = 'idle';
  #tentativas = 0;
  #timerReconexao: number | null = null;
  #timerEstabilidade: number | null = null;
  #encerradoDeProposito = false;

  #ultimoSeq: number;
  /** ids já entregues, em janela limitada — o Set responde, o array expira */
  readonly #vistos = new Set<string>();
  readonly #ordemVistos: string[] = [];

  readonly #fila: ClientMessage[] = [];

  readonly #porTipo = new Map<U1EventType, Set<EventHandler<unknown>>>();
  readonly #todos = new Set<EventHandler<unknown>>();
  readonly #status_handlers = new Set<StatusHandler>();

  /** tipos desconhecidos já reportados — avisar uma vez basta */
  readonly #desconhecidosAvisados = new Set<string>();

  constructor(opcoes: EventClientOptions) {
    this.#url = opcoes.url ?? urlDoStream();
    this.#sessionId = opcoes.sessionId;
    this.#ticket = opcoes.ticket;
    this.#backoff = { ...BACKOFF_PADRAO, ...opcoes.backoff };
    this.#maxDedupe = opcoes.maxDedupe ?? MAX_DEDUPE_PADRAO;
    this.#maxFila = opcoes.maxFila ?? MAX_FILA_PADRAO;
    this.#ultimoSeq = opcoes.since ?? 0;
    this.#renovar = opcoes.renovar;
    this.#onSessaoTrocada = opcoes.onSessaoTrocada;
    this.#onControl = opcoes.onControl;
  }

  get status(): ConnectionStatus {
    return this.#status;
  }

  /** Maior `seq` já aplicado. É o ponto de retomada. */
  get ultimoSeq(): number {
    return this.#ultimoSeq;
  }

  get pendentes(): number {
    return this.#fila.length;
  }

  // -- inscrições ----------------------------------------------------------

  /**
   * Assina um tipo de evento. O parâmetro `P` descreve o payload esperado pelo
   * chamador — o contrato tipa o envelope e deixa o payload como `unknown`,
   * então a forma do corpo é responsabilidade de quem lê (ver `payloads.ts`).
   */
  subscribe<P = unknown>(tipo: U1EventType, handler: EventHandler<P>): Unsubscribe {
    let conjunto = this.#porTipo.get(tipo);
    if (!conjunto) {
      conjunto = new Set();
      this.#porTipo.set(tipo, conjunto);
    }
    // O payload é `unknown` na borda e `P` para quem assinou; a conversão é a
    // fronteira entre os dois e está confinada a esta linha.
    const guardado = handler as unknown as EventHandler<unknown>;
    conjunto.add(guardado);
    return () => {
      conjunto.delete(guardado);
    };
  }

  /** Assina todos os eventos — útil para log, painel de depuração e telemetria. */
  subscribeAll(handler: EventHandler): Unsubscribe {
    this.#todos.add(handler);
    return () => {
      this.#todos.delete(handler);
    };
  }

  onStatus(handler: StatusHandler): Unsubscribe {
    this.#status_handlers.add(handler);
    // entrega o estado corrente na hora, para a interface não começar em branco
    handler(this.#status);
    return () => {
      this.#status_handlers.delete(handler);
    };
  }

  // -- ciclo de vida -------------------------------------------------------

  connect(): void {
    this.#encerradoDeProposito = false;
    if (this.#socket && (this.#socket.readyState === WebSocket.OPEN || this.#socket.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.#abrir();
    // Voltar a ter rede é sinal melhor que qualquer temporizador: reconecta já.
    window.addEventListener('online', this.#aoVoltarRede);
  }

  close(codigo = 1000, motivo = 'encerrado pelo cliente'): void {
    this.#encerradoDeProposito = true;
    this.#cancelarTimers();
    window.removeEventListener('online', this.#aoVoltarRede);
    const s = this.#socket;
    this.#socket = null;
    if (s && (s.readyState === WebSocket.OPEN || s.readyState === WebSocket.CONNECTING)) {
      s.close(codigo, motivo);
    }
    this.#definirStatus('closed', motivo);
  }

  /**
   * Envia uma mensagem ao runtime. Se a conexão não estiver aberta, a mensagem
   * entra na fila e sai no próximo `open` — na ordem em que foi produzida.
   */
  send(mensagem: ClientMessage): void {
    if (this.#socket && this.#socket.readyState === WebSocket.OPEN) {
      this.#socket.send(JSON.stringify(mensagem));
      return;
    }
    if (this.#fila.length >= this.#maxFila) {
      // Descartar o mais antigo: numa conversa, o turno velho já perdeu o
      // contexto; o recente ainda faz sentido quando a conexão voltar.
      const descartada = this.#fila.shift();
      console.warn('[u1] fila de envio cheia, mensagem mais antiga descartada:', descartada?.type);
    }
    this.#fila.push(mensagem);
  }

  // -- interno -------------------------------------------------------------

  /**
   * Abre — comprando credencial antes, se a tentativa anterior nem chegou a
   * abrir. É aqui que o laço de `1006` encontra a saída.
   */
  #abrir(): void {
    if (this.#renovarAntes && this.#renovar !== undefined && !this.#renovando) {
      this.#renovarAntes = false;
      this.#renovando = true;
      this.#definirStatus('reconnecting');
      void this.#renovar(this.#sessionId)
        .then(({ ticket, sessionId }) => {
          this.#ticket = ticket;
          if (sessionId !== this.#sessionId) {
            // Sessão nova: o `seq` da antiga não significa nada aqui, e pedir
            // `since=` de outra sessão faria o runtime devolver o que não é
            // desta conversa.
            this.#sessionId = sessionId;
            this.#ultimoSeq = 0;
            this.#vistos.clear();
            this.#ordemVistos.length = 0;
            console.info('[u1] a sessão anterior morreu; seguindo em', sessionId);
            this.#onSessaoTrocada?.(sessionId);
          }
        })
        .catch((causa: unknown) => {
          // Renovar falhou: tenta com o que tem. Se for ticket vencido, o
          // próximo fechamento sem abertura pede renovação de novo, e o
          // backoff é quem impede que isso vire enxurrada.
          console.warn('[u1] não consegui renovar a credencial:', causa);
        })
        .finally(() => {
          this.#renovando = false;
          if (!this.#encerradoDeProposito) this.#abrirSocket();
        });
      return;
    }
    this.#abrirSocket();
  }

  #abrirSocket(): void {
    this.#definirStatus(this.#tentativas === 0 ? 'connecting' : 'reconnecting');

    let socket: WebSocket;
    try {
      socket = new WebSocket(this.#montarUrl());
    } catch (causa) {
      console.warn('[u1] não foi possível abrir o WebSocket:', causa);
      this.#agendarReconexao();
      return;
    }
    this.#socket = socket;
    // Um fechamento sem abertura é upgrade recusado — quase sempre credencial
    // vencida. O navegador não deixa ler o status do handshake, então esta
    // bandeira é a única evidência disponível.
    let abriu = false;

    socket.onopen = () => {
      abriu = true;
      this.#definirStatus('open');
      this.#escoarFila();
      // O backoff só zera depois que a conexão provar que se sustenta.
      this.#timerEstabilidade = window.setTimeout(() => {
        this.#tentativas = 0;
        this.#timerEstabilidade = null;
      }, this.#backoff.estabilidadeMs);
    };

    socket.onmessage = (evento: MessageEvent<unknown>) => {
      this.#receber(evento.data);
    };

    socket.onerror = () => {
      // `onerror` não traz detalhe útil no navegador; o `onclose` seguinte é
      // quem carrega código e motivo. Aqui só não deixamos passar em silêncio.
      console.warn('[u1] erro no stream; aguardando o fechamento para reconectar.');
    };

    socket.onclose = (evento: CloseEvent) => {
      if (this.#socket === socket) this.#socket = null;
      if (this.#encerradoDeProposito) return;
      console.warn(`[u1] stream fechado (${evento.code}) ${evento.reason || 'sem motivo informado'}`);
      if (!abriu) this.#renovarAntes = true;
      this.#agendarReconexao();
    };
  }

  #montarUrl(): string {
    const url = new URL(this.#url);
    url.searchParams.set('session', this.#sessionId);
    url.searchParams.set('ticket', this.#ticket);
    // `since` só na retomada: sem ele o runtime entrega a sessão do zero.
    if (this.#ultimoSeq > 0) url.searchParams.set('since', String(this.#ultimoSeq));
    return url.toString();
  }

  #agendarReconexao(): void {
    if (this.#timerReconexao !== null) return;
    this.#cancelarEstabilidade();
    this.#definirStatus('reconnecting');

    const bruto = this.#backoff.baseMs * this.#backoff.fator ** this.#tentativas;
    const teto = Math.min(bruto, this.#backoff.maxMs);
    // Ruído de ±20%: evita que todos os clientes voltem no mesmo milissegundo
    // depois de um deploy e derrubem o runtime de novo.
    const espera = Math.round(teto * (0.8 + Math.random() * 0.4));
    this.#tentativas += 1;

    this.#timerReconexao = window.setTimeout(() => {
      this.#timerReconexao = null;
      if (!this.#encerradoDeProposito) this.#abrir();
    }, espera);
  }

  readonly #aoVoltarRede = (): void => {
    if (this.#encerradoDeProposito) return;
    if (this.#socket && this.#socket.readyState === WebSocket.OPEN) return;
    this.#cancelarTimers();
    this.#tentativas = 0;
    this.#abrir();
  };

  #escoarFila(): void {
    const socket = this.#socket;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    while (this.#fila.length > 0) {
      const mensagem = this.#fila.shift();
      if (mensagem) socket.send(JSON.stringify(mensagem));
    }
  }

  #receber(dado: unknown): void {
    if (typeof dado !== 'string') {
      // Frames binários não fazem parte do contrato do stream — áudio anda por
      // outro caminho (WebRTC). Ignorar é a leitura correta.
      return;
    }

    let analisado: unknown;
    try {
      analisado = JSON.parse(dado);
    } catch {
      console.warn('[u1] frame do stream não é JSON válido; descartado.');
      return;
    }

    // O runtime pode agrupar eventos num array durante a retomada.
    const lote = Array.isArray(analisado) ? analisado : [analisado];
    for (const item of lote) this.#despachar(item);
  }

  #despachar(item: unknown): void {
    if (typeof item !== 'object' || item === null) return;

    // Quadros de controle vêm antes da validação de envelope: eles não são
    // eventos U1 e não têm envelope (ver `events/wire.ts` no servidor). Passá-los
    // por `assertEnvelope` transformaria o funcionamento normal do stream num
    // erro no console a cada conexão.
    const tipo = (item as { type?: unknown }).type;
    if (typeof tipo === 'string' && tipo.startsWith('u1.control.')) {
      this.#controlar(tipo, item as Record<string, unknown>);
      return;
    }

    // O payload é lido antes da asserção: `assertEnvelope` estreita `candidato`
    // para `U1Envelope`, que não declara `payload` — o contrato separa envelope
    // de corpo de propósito, e o corpo é `unknown` para quem o consome.
    const corpo: unknown = (item as { payload?: unknown }).payload;
    const candidato = item as Partial<U1Envelope>;
    try {
      assertEnvelope(candidato);
    } catch (causa) {
      // Envelope inválido é divergência de contrato, exatamente o que a
      // auditoria encontrou. Precisa aparecer, não ser tolerado em silêncio.
      console.error('[u1]', causa instanceof Error ? causa.message : causa, item);
      return;
    }

    if (!isU1Event(candidato.type)) {
      if (!this.#desconhecidosAvisados.has(candidato.type)) {
        this.#desconhecidosAvisados.add(candidato.type);
        console.warn(`[u1] tipo de evento desconhecido: ${candidato.type} (ignorado)`);
      }
      // **Descartar um evento não é motivo para não reconhecer que ele passou.**
      //
      // Este `return` acontecia ANTES de avançar o ponto de retomada, e o efeito
      // só aparece na reconexão: um evento ignorado deixa `#ultimoSeq` para trás
      // do banco, então toda reconexão pede `since` de antes dele e recebe a
      // mesma cauda de replay outra vez — para descartá-la de novo. Não é laço
      // infinito (qualquer evento conhecido posterior destrava), mas é trabalho
      // repetido e um contador que mente sobre o que o cliente já viu.
      //
      // Encontrado ao renomear `u1.mission.updated`, e vale para os dois lados
      // do deploy: cliente velho recebendo nome novo, e cliente novo lendo do
      // replay as linhas antigas que ficaram em `session_events`. O segundo caso
      // é o que dura — ele não fecha quando todos atualizarem o bundle, só
      // quando a janela de replay drenar.
      //
      // Marcar como visto junto: um evento que não será processado nunca deve
      // voltar a custar decisão.
      this.#marcarVisto(candidato.event_id);
      if (candidato.seq > this.#ultimoSeq) this.#ultimoSeq = candidato.seq;
      return;
    }

    if (this.#vistos.has(candidato.event_id)) return; // replay
    this.#marcarVisto(candidato.event_id);

    // `seq` é monotônico por sessão; guardamos o maior porque a retomada pode
    // entregar fora de ordem e regredir o ponto de retomada perderia eventos.
    if (candidato.seq > this.#ultimoSeq) this.#ultimoSeq = candidato.seq;

    const evento: U1Event = { ...candidato, payload: corpo };

    for (const handler of this.#porTipo.get(evento.type) ?? []) this.#chamar(handler, evento);
    for (const handler of this.#todos) this.#chamar(handler, evento);
  }

  /** Quadros `u1.control.*`: renovação de ticket, fim de replay e recusas. */
  #controlar(tipo: string, quadro: Record<string, unknown>): void {
    // Antes dos `if` abaixo, e não depois: cada um deles termina em `return`, e
    // um aviso pendurado no fim da função só alcançaria os tipos que ninguém
    // trata — que é a defesa certa colocada no lugar onde ela não vale. Um
    // assinante que estoura não pode derrubar a renovação de ticket que vem
    // logo em seguida.
    if (this.#onControl !== undefined) {
      try {
        this.#onControl(tipo, quadro);
      } catch (causa) {
        console.error(`[u1] handler de ${tipo} lançou exceção:`, causa);
      }
    }

    if (tipo === 'u1.control.ticket') {
      const ticket = quadro['ticket'];
      // Trocar o ticket é o que mantém a reconexão possível com TTL curto.
      if (typeof ticket === 'string' && ticket !== '') this.#ticket = ticket;
      return;
    }

    if (tipo === 'u1.control.replay_done') {
      // `gap` significa que o buffer do servidor já descartou o que faltava:
      // a linha do tempo tem furo, e fingir que não tem é pior que dizer.
      if (quadro['gap'] === true) {
        console.warn('[u1] retomada com lacuna: eventos foram perdidos entre a queda e a volta.');
      }
      return;
    }

    if (tipo === 'u1.control.rejected') {
      console.warn(`[u1] mensagem recusada pelo runtime: ${String(quadro['reason'])}`, quadro['detail'] ?? '');
      return;
    }
  }

  #chamar(handler: EventHandler<unknown>, evento: U1Event): void {
    try {
      handler(evento);
    } catch (causa) {
      // Um assinante que estoura não pode impedir os outros de receber.
      console.error(`[u1] handler de ${evento.type} lançou exceção:`, causa);
    }
  }

  #marcarVisto(id: string): void {
    this.#vistos.add(id);
    this.#ordemVistos.push(id);
    if (this.#ordemVistos.length > this.#maxDedupe) {
      const antigo = this.#ordemVistos.shift();
      if (antigo !== undefined) this.#vistos.delete(antigo);
    }
  }

  #definirStatus(status: ConnectionStatus, detalhe?: string): void {
    if (this.#status === status) return;
    this.#status = status;
    for (const handler of this.#status_handlers) {
      try {
        handler(status, detalhe);
      } catch (causa) {
        console.error('[u1] handler de status lançou exceção:', causa);
      }
    }
  }

  #cancelarEstabilidade(): void {
    if (this.#timerEstabilidade !== null) {
      window.clearTimeout(this.#timerEstabilidade);
      this.#timerEstabilidade = null;
    }
  }

  #cancelarTimers(): void {
    this.#cancelarEstabilidade();
    if (this.#timerReconexao !== null) {
      window.clearTimeout(this.#timerReconexao);
      this.#timerReconexao = null;
    }
  }
}

/** Açúcar: cria e já conecta. */
export function criarEventClient(opcoes: EventClientOptions): EventClient {
  const cliente = new EventClient(opcoes);
  cliente.connect();
  return cliente;
}
